import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { request } from "../../utils/config"
import {
  Delivery,
  DeliveryInput,
  DeliveryStatusUpdateInput,
  DriverInput,
  FleetDriver,
  FleetVehicle,
  LogisticsDashboardStats,
  VehicleInput,
  AssignDriverInput,
} from "./types/deliveries"

// ==========================================
// RAW API CALLERS
// ==========================================

export const GET_DELIVERIES = async (params?: {
  status?: number
  driver?: string
  driverId?: number
  /** Haydovchi ismi, mashina raqami, telefon yoki yuk bo'yicha qidiruv */
  search?: string
  warehouse?: string
  warehouseName?: string
  /** Sana oralig'i bo'yicha filtr (chiqish vaqti) */
  begin?: string
  end?: string
  /** Qabul qiluvchi bo'yicha filtr */
  recipient?: string
}): Promise<Delivery[]> => {
  const res = await request({
    url: "Deliveries",
    params,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data || []
}

export const GET_LOGISTICS_DASHBOARD = async (): Promise<LogisticsDashboardStats> => {
  const res = await request({
    url: "Deliveries/Dashboard",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const GET_FLEET_DRIVERS = async (): Promise<FleetDriver[]> => {
  const res = await request({
    url: "Deliveries/Drivers",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data || []
}

export const CREATE_FLEET_DRIVER = async (data: DriverInput): Promise<FleetDriver> => {
  const res = await request({
    url: "Deliveries/Drivers",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const UPDATE_FLEET_DRIVER = async (data: DriverInput): Promise<FleetDriver> => {
  const res = await request({
    url: `Deliveries/Drivers/${data.id}`,
    method: "PUT",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const DELETE_FLEET_DRIVER = async (id: number): Promise<{ success: boolean }> => {
  const res = await request({
    url: `Deliveries/Drivers/${id}`,
    method: "DELETE",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

export const GET_FLEET_VEHICLES = async (): Promise<FleetVehicle[]> => {
  const res = await request({
    url: "Deliveries/Vehicles",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data || []
}

export const CREATE_FLEET_VEHICLE = async (data: VehicleInput): Promise<FleetVehicle> => {
  const res = await request({
    url: "Deliveries/Vehicles",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const UPDATE_FLEET_VEHICLE = async (data: VehicleInput): Promise<FleetVehicle> => {
  const res = await request({
    url: `Deliveries/Vehicles/${data.id}`,
    method: "PUT",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const DELETE_FLEET_VEHICLE = async (id: number): Promise<{ success: boolean }> => {
  const res = await request({
    url: `Deliveries/Vehicles/${id}`,
    method: "DELETE",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

export const GET_DELIVERY_BY_ID = async (id: number): Promise<Delivery> => {
  const res = await request({
    url: `Deliveries/${id}`,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const CREATE_DELIVERY = async (data: DeliveryInput): Promise<Delivery> => {
  const res = await request({
    url: "Deliveries",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const UPDATE_DELIVERY = async (data: DeliveryInput): Promise<Delivery> => {
  const res = await request({
    url: `Deliveries/${data.id}`,
    method: "PUT",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const UPDATE_DELIVERY_STATUS = async (
  data: DeliveryStatusUpdateInput
): Promise<Delivery> => {
  const res = await request({
    url: "Deliveries/UpdateStatus",
    method: "PUT",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const DELETE_DELIVERY = async (id: number): Promise<{ success: boolean }> => {
  const res = await request({
    url: `Deliveries/${id}`,
    method: "DELETE",
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

export const ASSIGN_DRIVER = async (data: AssignDriverInput): Promise<Delivery> => {
  const res = await request({
    url: "Deliveries/Assign",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const UPDATE_LOADING_PROGRESS = async (
  data: { id: number; loadingProgress: number; warehouseName?: string }
): Promise<Delivery> => {
  const res = await request({
    url: "Deliveries/UpdateProgress",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data?.data || res.data
}

export const SIGN_AND_DELIVER = async (
  data: { id: number; signatureImage: string; paidAmountUSD?: number; paymentType?: string }
): Promise<{ success: boolean; message: string; data?: Delivery }> => {
  const res = await request({
    url: "Deliveries/SignAndDeliver",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

export const VERIFY_DELIVERY_OTP = async (
  data: DeliveryStatusUpdateInput
): Promise<{ success: boolean; message: string }> => {
  const res = await request({
    url: "Deliveries/VerifyOtp",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

/**
 * «Tasdiqlash» — qabul qiluvchi yukni olganini tasdiqlaydi va buyurtma
 * «Yetkazildi» holatiga o'tadi (texnik talablar, 2-bo'lim).
 */
export const CONFIRM_DELIVERY_RECEIPT = async (data: {
  id: number
  confirmedBy?: string
}): Promise<{ success: boolean; message: string; data?: Delivery }> => {
  const res = await request({
    url: "Deliveries/ConfirmReceipt",
    method: "POST",
    data,
    Headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

export const SEED_DELIVERY_DATA = async (): Promise<any> => {
  const res = await request({
    url: "Deliveries/SeedInitialData",
    method: "POST",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
    },
  })
  return res.data
}

// ==========================================
// REACT QUERY HOOKS
// ==========================================

export const useDeliveriesQuery = (params?: {
  status?: number
  driverId?: number
  warehouseName?: string
}) => {
  return useQuery(["deliveries", params], () => GET_DELIVERIES(params), {
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })
}

export const useLogisticsDashboardQuery = () => {
  return useQuery(["logistics_dashboard"], () => GET_LOGISTICS_DASHBOARD(), {
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })
}

export const useFleetDriversQuery = () => {
  return useQuery(["fleet_drivers"], () => GET_FLEET_DRIVERS(), {
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })
}

export const useFleetVehiclesQuery = () => {
  return useQuery(["fleet_vehicles"], () => GET_FLEET_VEHICLES(), {
    refetchOnWindowFocus: false,
    staleTime: 30000,
  })
}

export const useCreateDriverMutation = () => {
  const qc = useQueryClient()
  return useMutation(CREATE_FLEET_DRIVER, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useUpdateDriverMutation = () => {
  const qc = useQueryClient()
  return useMutation(UPDATE_FLEET_DRIVER, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["logistics_dashboard"])
      qc.invalidateQueries(["deliveries"])
    },
  })
}

export const useDeleteDriverMutation = () => {
  const qc = useQueryClient()
  return useMutation(DELETE_FLEET_DRIVER, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["logistics_dashboard"])
      qc.invalidateQueries(["deliveries"])
    },
  })
}

export const useCreateVehicleMutation = () => {
  const qc = useQueryClient()
  return useMutation(CREATE_FLEET_VEHICLE, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useUpdateVehicleMutation = () => {
  const qc = useQueryClient()
  return useMutation(UPDATE_FLEET_VEHICLE, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["logistics_dashboard"])
      qc.invalidateQueries(["deliveries"])
    },
  })
}

export const useDeleteVehicleMutation = () => {
  const qc = useQueryClient()
  return useMutation(DELETE_FLEET_VEHICLE, {
    onSuccess: () => {
      qc.invalidateQueries(["fleet_vehicles"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["logistics_dashboard"])
      qc.invalidateQueries(["deliveries"])
    },
  })
}

export const useAssignDriverMutation = () => {
  const qc = useQueryClient()
  return useMutation(ASSIGN_DRIVER, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useUpdateLoadingProgressMutation = () => {
  const qc = useQueryClient()
  return useMutation(UPDATE_LOADING_PROGRESS, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useSignAndDeliverMutation = () => {
  const qc = useQueryClient()
  return useMutation(SIGN_AND_DELIVER, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useCreateDeliveryMutation = () => {
  const qc = useQueryClient()
  return useMutation(CREATE_DELIVERY, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useUpdateDeliveryMutation = () => {
  const qc = useQueryClient()
  return useMutation(UPDATE_DELIVERY, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

export const useDeleteDeliveryMutation = () => {
  const qc = useQueryClient()
  return useMutation(DELETE_DELIVERY, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["logistics_dashboard"])
      qc.invalidateQueries(["fleet_drivers"])
      qc.invalidateQueries(["fleet_vehicles"])
    },
  })
}

export const useVerifyOtpMutation = () => {
  const qc = useQueryClient()
  return useMutation(VERIFY_DELIVERY_OTP, {
    onSuccess: () => {
      qc.invalidateQueries(["deliveries"])
      qc.invalidateQueries(["logistics_dashboard"])
    },
  })
}

// ==========================================
// MIJOZGA YUBORILADIGAN OCHIQ INVOYS
// ==========================================

/** Yuk uchun ochiq havola kalitini oladi (bo'lmasa backend yaratadi). */
export const CREATE_DELIVERY_PUBLIC_LINK = async (
  id: number
): Promise<{ success: boolean; data?: { token: string } }> => {
  const res = await request({
    url: `Deliveries/PublicLink/${id}`,
    method: "POST",
  })
  return res.data
}

/**
 * Mijoz ko'radigan invoys. Bu so'rov login talab qilmaydi — sahifa
 * havolani bilgan har qanday odam uchun ochiladi.
 */
export const GET_PUBLIC_INVOICE = async (
  token: string
): Promise<{ success: boolean; data?: any }> => {
  const res = await request({
    url: `Deliveries/Public/${token}`,
    method: "GET",
    silent: true,
  })
  return res.data
}

/** Mijoz qabul qilganini tasdiqlaydi — login talab qilmaydi. */
export const CONFIRM_PUBLIC_INVOICE = async (params: {
  token: string
  confirmedBy?: string
}): Promise<{ success: boolean; message?: string; data?: any }> => {
  const res = await request({
    url: `Deliveries/Public/${params.token}/Confirm`,
    method: "POST",
    data: { confirmedBy: params.confirmedBy },
  })
  return res.data
}
